/**
 * Local pointer to "the draft request this browser is mid-conversation on".
 *
 * The words are never lost — the request row exists server-side the moment
 * Send is pressed — but it now stays in `drafting` and out of Posts until
 * intake settles, so there is nothing in the ordinary request list a client
 * could use to find their way back to an unfinished conversation. This is
 * the one piece of client-only state in the whole feature, and it exists
 * purely so that closing the tab mid-conversation and reopening it lands the
 * client back in the same conversation instead of an empty box or the
 * unsettling sense that their post vanished.
 *
 * Deliberately per-browser, not per-account: a second device or a private
 * window simply won't resume the draft, which is a strictly better failure
 * mode than inventing a synced "my drafts" endpoint outside the given
 * contract. The request itself is always safely on the server regardless.
 */

interface DraftPointer {
  readonly requestId: string;
  /** Carried alongside the id so the panel can show "#N" immediately on
   * resume without a round trip just to learn the number. */
  readonly number: number;
}

function storageKey(workspaceId: string): string {
  return `rundown:draft:${workspaceId}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Null whenever there is no draft, storage is unavailable, or the stored value is malformed. */
export function getDraft(workspaceId: string): DraftPointer | null {
  try {
    const raw = localStorage.getItem(storageKey(workspaceId));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (isRecord(parsed) && typeof parsed['requestId'] === 'string' && typeof parsed['number'] === 'number') {
      return { requestId: parsed['requestId'], number: parsed['number'] };
    }
    return null;
  } catch {
    // A private window, cleared or blocked site data, or a corrupt value all
    // land here — treated the same as "no draft", never as an error the
    // client should see.
    return null;
  }
}

export function setDraft(workspaceId: string, draft: DraftPointer): void {
  try {
    localStorage.setItem(storageKey(workspaceId), JSON.stringify(draft));
  } catch {
    // Best-effort: if this fails, resuming after a closed tab silently stops
    // working, but the request itself is still safe on the server — nothing
    // about posting or intake depends on this succeeding.
  }
}

export function clearDraft(workspaceId: string): void {
  try {
    localStorage.removeItem(storageKey(workspaceId));
  } catch {
    // See setDraft.
  }
}
